import React, { useState } from 'react';
import { FaTrash, FaPlay, FaCropAlt, FaTimes } from 'react-icons/fa';
import ClipTimestampField from './ClipTimestampField';
import ClipHintDrawer from './ClipHintDrawer';
import './crop-studio.css';

export default function ClipCard({
  clip,
  clipIndex,
  cutHint = null,
  timelineCuts = [],
  activeField,
  onActivateField,
  onSaveField,
  onCancelField,
  onSeek,
  onDelete,
  onEditCrop,
  onRemoveCrop,
  onMuteToggle,
  onEffectChange,
  onPreview,
  onNudgeTime,
  isPreviewing = false,
  showMute = false,
  showEffect = false,
  showTag = false,
  tagEnabled = false,
  onTagToggle,
  tagValue = '',
  onTagChange,
}) {
  const [hintOpen, setHintOpen] = useState(false);
  const tagLabel = (clip.tag || cutHint?.tag || '').trim();

  const rangeLabel = [clip.startTime || '—', clip.endTime || '…'].join(' → ');
  const canPreview = !!(
    (clip.startTime && clip.endTime)
    || (cutHint?.range?.start && cutHint?.range?.end)
  );
  const isStartActive = activeField?.clipIndex === clipIndex && activeField?.field === 'startTime';
  const isEndActive = activeField?.clipIndex === clipIndex && activeField?.field === 'endTime';

  const handleNudge = (field, deltaSec) => {
    onNudgeTime?.(clipIndex, field, deltaSec);
  };

  const toggleHint = () => {
    setHintOpen(v => !v);
  };

  return (
    <article
      className={[
        'clip-card',
        isStartActive || isEndActive ? 'clip-card--editing' : '',
        isPreviewing ? 'clip-card--previewing' : '',
        hintOpen ? 'clip-card--hint-open' : '',
      ].filter(Boolean).join(' ')}
      data-clip-index={clipIndex}
    >
      <header className="clip-card__header">
        <div className="clip-card__title">
          <div className="clip-card__title-row">
            <span className="clip-card__index">Clip {clipIndex + 1}</span>
            {tagLabel && (
              <span className="clip-card__tag-badge" title="Timeline tag">{tagLabel}</span>
            )}
            {clip.crop && (
              <span className="clip-card__crop-badge">
                <button
                  type="button"
                  onClick={() => onEditCrop?.(clipIndex)}
                  title={onEditCrop ? 'Edit crop' : 'Cropped'}
                  disabled={!onEditCrop}
                >
                  <FaCropAlt aria-hidden="true" />
                  {clip.crop.width}×{clip.crop.height}
                </button>
                {onRemoveCrop && (
                  <button
                    type="button"
                    className="clip-card__crop-remove"
                    onClick={() => onRemoveCrop(clipIndex)}
                    aria-label={`Remove crop from clip ${clipIndex + 1}`}
                    title="Remove crop"
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                )}
              </span>
            )}
            {cutHint && (
              <button
                type="button"
                className={`clip-card__hint-badge ${hintOpen ? 'clip-card__hint-badge--open' : ''}`}
                onClick={toggleHint}
                aria-expanded={hintOpen}
                title="Show cut hint"
              >
                Hint
              </button>
            )}
          </div>
          <span className="clip-card__range">{rangeLabel}</span>
        </div>
        <div className="clip-card__header-actions">
          {canPreview && (
            <button
              type="button"
              className={`clip-card__preview ${isPreviewing ? 'clip-card__preview--active' : ''}`}
              onClick={() => onPreview?.(clipIndex)}
              title="Play this clip range on the source video"
            >
              <FaPlay aria-hidden="true" />
              <span>{isPreviewing ? 'Playing…' : 'Preview'}</span>
            </button>
          )}
          {onEditCrop && !clip.crop && (
            <button
              type="button"
              className="clip-card__crop-btn"
              onClick={() => onEditCrop(clipIndex)}
              title={`Crop clip ${clipIndex + 1}`}
            >
              <FaCropAlt aria-hidden="true" />
              <span>Crop</span>
            </button>
          )}
          <button
            type="button"
            className="clip-card__delete"
            onClick={() => onDelete?.(clipIndex)}
            title={`Delete clip ${clipIndex + 1}`}
          >
            <FaTrash aria-hidden="true" />
            <span>Delete</span>
          </button>
        </div>
      </header>

      <ClipHintDrawer
        open={hintOpen}
        onClose={() => setHintOpen(false)}
        cutHint={cutHint}
        tag={tagLabel}
        timelineCuts={timelineCuts}
        clipIndex={clipIndex}
        clipStartTime={clip.startTime}
        clipEndTime={clip.endTime}
        activeField={activeField}
        onActivateField={onActivateField}
        onSaveField={onSaveField}
        onCancelField={onCancelField}
        onSeek={onSeek}
        onNudgeTime={onNudgeTime}
      />

      <div className="clip-card__body">
        <ClipTimestampField
          label="Start"
          kind="start"
          field="startTime"
          value={clip.startTime}
          pairedValue={clip.endTime}
          isActive={isStartActive}
          previewTime={isStartActive ? activeField?.previewTime : null}
          onActivate={() => onActivateField?.(clipIndex, 'startTime')}
          onSave={onSaveField}
          onCancel={onCancelField}
          onSeek={onSeek}
          onNudge={onNudgeTime ? handleNudge : undefined}
        />

        <ClipTimestampField
          label="End"
          kind="end"
          field="endTime"
          value={clip.endTime}
          pairedValue={clip.startTime}
          isActive={isEndActive}
          previewTime={isEndActive ? activeField?.previewTime : null}
          onActivate={() => onActivateField?.(clipIndex, 'endTime')}
          onSave={onSaveField}
          onCancel={onCancelField}
          onSeek={onSeek}
          onNudge={onNudgeTime ? handleNudge : undefined}
        />

        {(showMute || showEffect || showTag) && (
          <div className="clip-card__extras">
            {showMute && (
              <label className="clip-card__mute">
                <input
                  type="checkbox"
                  checked={!!clip.mute}
                  onChange={() => onMuteToggle?.(clipIndex)}
                />
                <span>Mute clip audio</span>
              </label>
            )}

            {showEffect && (
              <div className="clip-card__effect">
                <label htmlFor={`clip-effect-${clipIndex}`}>Effect</label>
                <select
                  id={`clip-effect-${clipIndex}`}
                  value={clip.effect || ''}
                  onChange={e => onEffectChange?.(clipIndex, e.target.value)}
                >
                  <option value="">None</option>
                  <option value="zoom">Zoom</option>
                  <option value="mirror">Mirror</option>
                  <option value="zoom+mirror">Zoom + Mirror</option>
                </select>
              </div>
            )}

            {showTag && (
              <div className="clip-card__tag">
                <button
                  type="button"
                  className={`clip-tag-toggle ${tagEnabled ? 'is-on' : ''}`}
                  onClick={e => {
                    e.stopPropagation();
                    onTagToggle?.(clipIndex);
                  }}
                  onTouchEnd={e => e.stopPropagation()}
                  aria-pressed={tagEnabled}
                  aria-label={tagEnabled ? 'Disable clip tag' : 'Enable clip tag'}
                >
                  <span className="clip-tag-toggle__track" aria-hidden="true">
                    <span className="clip-tag-toggle__thumb" />
                  </span>
                  <span className="clip-tag-toggle__label">Tag clip</span>
                </button>

                {tagEnabled && (
                  <div
                    className="segment-tag-input"
                    onClick={e => e.stopPropagation()}
                    onTouchEnd={e => e.stopPropagation()}
                  >
                    <div className="tag-input">
                      <input
                        className="to-do-input clip-tag-input"
                        placeholder="Tag this clip…"
                        value={tagValue}
                        type="text"
                        onChange={e => onTagChange?.(clipIndex, e.target.value)}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
